/**
 * Reading a localStorage key that has been RENAMED.
 *
 * The game was called Rocketweb and is now Vroomball, and four things it keeps
 * in the browser were keyed by the old name: the settings, the profile cache,
 * the Rocket Pass claims and the session token. Renaming those keys without
 * doing anything else is not a rename — it is a silent factory reset for every
 * player who already has them. Their bindings, their camera, their loadout and
 * their sign-in all still exist, sitting under a key nothing reads any more.
 *
 * So each key is read through here: the new name first, and the old name only
 * when the new one holds nothing. A value found under the old name is COPIED
 * FORWARD and the old key removed, which means the migration happens once, on
 * the first read after the update, and every read after that is the plain path.
 *
 * The copy is deliberately best-effort. If storage is full or the write throws,
 * the old value is still RETURNED — the player gets their settings this session
 * and the migration is retried next time, which beats failing the load over a
 * bookkeeping step. The old key is only removed once the new one is safely
 * written, so a crash between the two cannot lose the value.
 *
 * This type is structural rather than imported so this module has no dependency
 * on anything in the game; tests hand it a plain object.
 */
export type MigratingStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * `key`, falling back to `legacyKey` and migrating it forward.
 *
 * Returns null when neither exists. THE FIRST READ IS NOT GUARDED, on purpose:
 * callers already wrap this in a try/catch and use the throw to tell "storage
 * is broken" apart from "nothing is saved" — a Safari private window used to
 * throw on read, and some extensions still do. Swallowing it here would report
 * a wiped profile as an empty one, which is the same lie this whole module
 * exists to prevent. Everything on the legacy path IS guarded, because a
 * failure there is only ever the loss of a migration that can be retried.
 */
export const readMigrating = (
  storage: MigratingStorage,
  key: string,
  legacyKey: string
): string | null => {
  const current = storage.getItem(key);
  if (current !== null) return current;

  let legacy: string | null = null;
  try {
    legacy = storage.getItem(legacyKey);
  } catch {
    return null;
  }
  if (legacy === null) return null;

  try {
    storage.setItem(key, legacy);
    // Only after the new key is written. Removing first would turn a failed
    // write into a lost value.
    storage.removeItem(legacyKey);
  } catch {
    // Full, or disabled mid-session. The value is still good for this run.
  }
  return legacy;
};

/** The old key for a new one, for the four keys that were renamed. */
export const legacyKeyFor = (key: string): string => key.replace(/^vroomball\./, 'rocketweb.');
